import { GradientEditor } from '../../ui/GradientEditor.js';
import { ColorField, JsonField, SelectField } from '../../ui/controls.js';
import {
  GRADIENT_MODES,
  GRADIENT_MODE_LABELS,
  convertGradient,
  cssColor,
  gradientMode,
  isGradient,
  rgba,
  type Gradient,
  type GradientMode,
} from './values.js';
import './fields.css';

/** The reusable editor (components/ui/GradientEditor.tsx), re-exported for existing imports. */
export { GradientEditor };

const SHORT_LABELS: Record<GradientMode, string> = {
  color: 'Color',
  gradient: 'Gradient',
  randomColors: 'Random colors',
  randomGradients: 'Random gradients',
  randomColor: 'Random color',
};

/** Optional extras passed through to the gradient bars. */
export interface GradientFieldExtras {
  playhead?: number | null;
  onGestureStart?: () => void;
  onGestureEnd?: () => void;
}

/**
 * Editor for a MinMaxGradient value (`[r, g, b, a]`, `{ random: [c1, c2] }`,
 * `{ gradient }`, `{ gradient, random: true }` or `{ gradients: [g1, g2] }`)
 * with a mode selector, or, with `plain`, for a bare gradient object.
 */
export function GradientField({
  value,
  onChange,
  modes = GRADIENT_MODES,
  plain = false,
  extras = {},
}: {
  value: unknown;
  onChange: (value: unknown) => void;
  modes?: readonly GradientMode[];
  plain?: boolean;
  extras?: GradientFieldExtras;
}) {
  if (plain) {
    if (value !== undefined && !isGradient(value)) return <JsonField value={value} onChange={onChange} rows={2} />;
    return (
      <div className="curvefield">
        <GradientEditor value={value as Gradient | undefined} onChange={onChange} {...extras} />
      </div>
    );
  }

  const mode = gradientMode(value);
  if (mode === null) return <JsonField value={value} onChange={onChange} rows={2} />;
  const obj = (value ?? {}) as { random?: unknown; gradient?: Gradient; gradients?: [Gradient, Gradient] };
  const options = GRADIENT_MODES.filter((m) => m === mode || modes.includes(m)).map((m) => ({ value: m, label: SHORT_LABELS[m] }));
  const modeSelect = (
    <div className="curvefield-mode" title={GRADIENT_MODE_LABELS[mode]}>
      <SelectField value={mode} options={options} onChange={(m) => onChange(convertGradient(value, m as GradientMode))} />
    </div>
  );

  switch (mode) {
    case 'color':
      return (
        <div className="curvefield">
          <div className="curvefield-row">
            <ColorField value={value === undefined ? undefined : rgba(value)} onChange={onChange} />
            {modeSelect}
          </div>
        </div>
      );
    case 'randomColors': {
      const a = rgba((obj.random as unknown[])[0]);
      const b = rgba((obj.random as unknown[])[1]);
      return (
        <div className="curvefield">
          <div className="curvefield-row">
            <span className="curvefield-swatch-preview" style={{ backgroundImage: `linear-gradient(90deg, ${cssColor(a)}, ${cssColor(b)})` }} />
            {modeSelect}
          </div>
          <ColorField value={a} onChange={(c) => onChange({ ...obj, random: [rgba(c), b] })} />
          <ColorField value={b} onChange={(c) => onChange({ ...obj, random: [a, rgba(c)] })} />
        </div>
      );
    }
    case 'gradient':
    case 'randomColor':
      return (
        <div className="curvefield">
          <div className="curvefield-row">
            <span className="curve-editor-hint">{mode === 'randomColor' ? 'Sampled at a random time per particle' : 'Over time'}</span>
            {modeSelect}
          </div>
          <GradientEditor value={obj.gradient} onChange={(gradient) => onChange({ ...obj, gradient })} {...extras} />
        </div>
      );
    case 'randomGradients': {
      const [ga, gb] = obj.gradients!;
      return (
        <div className="curvefield">
          <div className="curvefield-row">
            <span className="curve-editor-hint">Random between gradient A and B</span>
            {modeSelect}
          </div>
          <GradientEditor value={ga} onChange={(g) => onChange({ ...obj, gradients: [g, gb] })} {...extras} />
          <GradientEditor value={gb} onChange={(g) => onChange({ ...obj, gradients: [ga, g] })} {...extras} />
        </div>
      );
    }
  }
}
